import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import NavigationBar from '../../components/ui/NavigationBar';
import ResultsHeader from './components/ResultsHeader';
import FilterControls from './components/FilterControls';
import FieldCard from './components/FieldCard';
import FieldDetailsModal from './components/FieldDetailsModal';
import ComparisonPanel from './components/ComparisonPanel';
import ActionButtons from './components/ActionButtons';
import EmptyState from './components/EmptyState';

const StudyFieldResults = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    interest: 'all',
    difficulty: 'all',
    demand: 'all',
    sort: 'compatibility'
  });

  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedFieldForDetails, setSelectedFieldForDetails] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    fetchRecommendations();
  }, [user]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        setFields([]);
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase?.from('user_recommendations')?.select(`
          score,
          fields (
            id,
            field_id,
            name,
            description,
            full_description,
            interest,
            difficulty,
            market_demand,
            salary_range,
            employment_rate,
            required_subjects,
            available_institutions,
            market_outlook,
            career_paths,
            skills_developed,
            field_data
          )
        `)?.eq('user_id', user?.id)?.order('score', { ascending: false })?.limit(5);

      if (fetchError) {
        console.error('Error fetching recommendations:', fetchError);
        setError(fetchError?.message);
        setFields([]);
      } else {
        const transformedFields = data?.map(rec => ({
          id: rec?.fields?.id,
          fieldId: rec?.fields?.field_id,
          name: rec?.fields?.name,
          description: rec?.fields?.description,
          fullDescription: rec?.fields?.full_description,
          compatibilityScore: rec?.score,
          careerProspects: rec?.fields?.field_data?.careerProspects || '',
          marketDemand: rec?.fields?.market_demand,
          difficulty: rec?.fields?.difficulty,
          salaryRange: rec?.fields?.salary_range,
          employmentRate: rec?.fields?.employment_rate,
          requiredSubjects: rec?.fields?.required_subjects || [],
          availableInstitutions: rec?.fields?.available_institutions,
          marketOutlook: rec?.fields?.market_outlook,
          careerPaths: rec?.fields?.career_paths || [],
          academicPath: rec?.fields?.field_data?.academicPath || [],
          skillsDeveloped: rec?.fields?.skills_developed || [],
          recommendedInstitutions: rec?.fields?.field_data?.recommendedInstitutions || [],
          interest: rec?.fields?.interest
        })) || [];

        setFields(transformedFields);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError(err?.message);
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      interest: 'all',
      difficulty: 'all',
      demand: 'all',
      sort: 'compatibility'
    });
  };

  const handleFieldSelect = (fieldId, isSelected) => {
    setSelectedFields(prev => {
      if (isSelected) {
        return [...prev, fieldId];
      } else {
        return prev?.filter(id => id !== fieldId);
      }
    });
  };

  const handleViewDetails = (field) => {
    setSelectedFieldForDetails(field);
  };

  const handleCloseDetails = () => {
    setSelectedFieldForDetails(null);
  };

  const handleShowComparison = () => {
    setShowComparison(true);
  };

  const handleCloseComparison = () => {
    setShowComparison(false);
  };

  const filteredAndSortedFields = useMemo(() => {
    let filtered = [...fields];

    if (filters?.interest !== 'all') {
      filtered = filtered?.filter(field => field?.interest === filters?.interest);
    }

    if (filters?.difficulty !== 'all') {
      filtered = filtered?.filter(field => field?.difficulty === filters?.difficulty);
    }

    if (filters?.demand !== 'all') {
      filtered = filtered?.filter(field => field?.marketDemand === filters?.demand);
    }

    filtered?.sort((a, b) => {
      switch (filters?.sort) {
        case 'compatibility':
          return b?.compatibilityScore - a?.compatibilityScore;
        case 'demand':
          const demandOrder = { high: 3, medium: 2, low: 1 };
          return demandOrder?.[b?.marketDemand] - demandOrder?.[a?.marketDemand];
        case 'salary':
          const getSalaryValue = (range) => {
            const match = range?.match(/(\d+)\s*000/);
            return match ? parseInt(match?.[1]) : 0;
          };
          return getSalaryValue(b?.salaryRange) - getSalaryValue(a?.salaryRange);
        case 'difficulty':
          const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
          return difficultyOrder?.[a?.difficulty] - difficultyOrder?.[b?.difficulty];
        default:
          return 0;
      }
    });

    return filtered;
  }, [filters, fields]);

  const selectedFieldsData = useMemo(() => {
    return fields?.filter(field => selectedFields?.includes(field?.id));
  }, [selectedFields, fields]);

  const currentDate = new Date()?.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Résultats de l'Orientation - CURSUS Orientation</title>
          <meta name="description" content="Découvrez vos filières recommandées basées sur votre profil et vos aspirations académiques au Sénégal" />
        </Helmet>
        <div className="min-h-screen bg-gray-50">
          <NavigationBar />
          <main className="content-area">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Chargement de vos recommandations...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Résultats de l'Orientation - CURSUS Orientation</title>
          <meta name="description" content="Découvrez vos filières recommandées basées sur votre profil et vos aspirations académiques au Sénégal" />
        </Helmet>
        <div className="min-h-screen bg-gray-50">
          <NavigationBar />
          <main className="content-area">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-800">Erreur: {error}</p>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Helmet>
          <title>Résultats de l'Orientation - CURSUS Orientation</title>
          <meta name="description" content="Découvrez vos filières recommandées basées sur votre profil et vos aspirations académiques au Sénégal" />
        </Helmet>
        <div className="min-h-screen bg-gray-50">
          <NavigationBar />
          <main className="content-area">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <p className="text-yellow-800">Veuillez vous connecter pour voir vos recommandations.</p>
                <button
                  onClick={() => navigate('/user-login')}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Se connecter
                </button>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (fields?.length === 0) {
    return (
      <>
        <Helmet>
          <title>Résultats de l'Orientation - CURSUS Orientation</title>
          <meta name="description" content="Découvrez vos filières recommandées basées sur votre profil et vos aspirations académiques au Sénégal" />
        </Helmet>
        <div className="min-h-screen bg-gray-50">
          <NavigationBar />
          <main className="content-area">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Aucune recommandation disponible</h2>
                  <p className="text-gray-600 mb-6">
                    Vous n'avez pas encore de recommandations de filières. Complétez notre quiz interactif pour découvrir les filières qui correspondent à votre profil.
                  </p>
                  <button
                    onClick={() => navigate('/interactive-quiz-system')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Commencer le quiz
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Résultats de l'Orientation - CURSUS Orientation</title>
        <meta name="description" content="Découvrez vos filières recommandées basées sur votre profil et vos aspirations académiques au Sénégal" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />

        <main className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <ResultsHeader
              studentName={user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Étudiant'}
              totalRecommendations={filteredAndSortedFields?.length}
              completionDate={currentDate}
            />

            <FilterControls
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              showComparison={handleShowComparison}
              selectedCount={selectedFields?.length}
            />

            <ActionButtons hasSelectedFields={selectedFields?.length > 0} />

            {filteredAndSortedFields?.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {filteredAndSortedFields?.map((field) => (
                  <FieldCard
                    key={field?.id}
                    field={field}
                    isSelected={selectedFields?.includes(field?.id)}
                    onSelect={handleFieldSelect}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </main>

        {selectedFieldForDetails && (
          <FieldDetailsModal
            field={selectedFieldForDetails}
            onClose={handleCloseDetails}
          />
        )}

        {showComparison && selectedFieldsData?.length >= 2 && (
          <ComparisonPanel
            fields={selectedFieldsData}
            onClose={handleCloseComparison}
          />
        )}
      </div>
    </>
  );
};

export default StudyFieldResults;